import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import {
  ForgeList,
  ForgeListItem,
  ForgeExpansionPanel,
  ForgeOpenIcon,
  ForgeIcon,
} from '@tylertech/forge-react';
import { Context } from '../../../store/Store.js';
import ConditionalTooltip from '../../reusable/ConditionalTooltip.js';
import {
  getAccessCondition,
  checkIfNotEmpty,
} from '../../reusable/reusableFunctions.js';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { useUser } from '../../../contexts/userContext.js';
import { NO_BUDGET } from '../../../static/constants.js';
import { buildPath } from '../../../services/navigateService.js';

const ExpandingNavItem = ({
  itemName,
  iconName,
  subItems,
  setSelected,
  selected,
  organizationAccessIcons,
}) => {
  const [state, dispatch] = useContext(Context);
  const navigate = useNavigate();
  const buttonRef = React.useRef(null);
  const expandingButtonRef = React.useRef(null);
  const { user } = useUser();

  const getLocation = (to, title) => {
    const orgId = user?.activeOrganization;
    const budgetId = user?.activeBudget?.id || NO_BUDGET;

    if (!orgId || !budgetId) return;
    const path = state.fireUnsavedChangesDialog || state.changesWereMade ? null : buildPath(user, to);

    dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
    setSelected(title);
    navigate(path);
  };

  const checkIfExpansionPanelIsOpen = () => {
    return subItems.some((item) => item.title === selected);
  };

  return (
    <div role="listitem">
      {checkIfNotEmpty(subItems, organizationAccessIcons, user) && (
        <ForgeExpansionPanel
          role="list"
          open={checkIfExpansionPanelIsOpen()}
          trigger={`${itemName.replaceAll(
            ' ',
            '-'
          )}-sub-nav-item-parent-button`}
        >
          <ForgeListItem
            slot="header"
            key={itemName}
            id={`${itemName.replaceAll(' ', '-')}-sub-nav-item-parent`}
          >
            <ForgeIcon
              external
              externalType="all"
              slot="start"
              name={iconName}
            />
            <button
              ref={expandingButtonRef}
              id={`${itemName.replaceAll(' ', '-')}-sub-nav-item-parent-button`}
            >
              {itemName}
            </button>
            <ForgeOpenIcon slot="end" />
          </ForgeListItem>
          <div role="listitem">
            <ForgeList indented>
              {subItems.map((item) => (
                getAccessCondition(item.name, organizationAccessIcons, user) && (
                  <ForgeListItem
                    key={item.title}
                    selected={selected === item.title}
                    id={`${item.title.replaceAll(' ', '-')}-sub-nav-item`}
                    className="clickable"
                    onClick={() => getLocation(item.to, item.title)}
                  >
                    <button ref={buttonRef} type="button">{item.title}</button>
                    <ConditionalTooltip
                      name={item.title}
                      targetRef={buttonRef}
                    />
                  </ForgeListItem>
                )
              ))}
            </ForgeList>
          </div>
        </ForgeExpansionPanel>
      )}
      <ConditionalTooltip
        name={itemName}
        targetRef={expandingButtonRef}
      />
    </div>
  );
};

ExpandingNavItem.propTypes = {
  itemName: PropTypes.string,
  iconName: PropTypes.string,
  subItems: PropTypes.array,
  setSelected: PropTypes.func,
  selected: PropTypes.string,
  organizationAccessIcons: PropTypes.array,
};

export default ExpandingNavItem;
